export const checkStatus: {
  checked: [boolean, boolean];
  unchecked: [boolean, boolean];
  indeterminate: [boolean, boolean];
} = {
  checked: [true, true],
  unchecked: [false, false],
  indeterminate: [true, false],
};

export enum Status {
  checked = 'checked',
  unchecked = 'unchecked',
  indeterminate = 'indeterminate'
}

export function checkSelectionsForEveryNode(nodes: NodeSet, selections: Selections) {
  for(const node of nodes) {
    const isSelected = isNodeSelected(selections, node);
    node.checked = isSelected ? checkStatus.checked : checkStatus.unchecked;

    if (isSelected) {
      node.checked = checkStatus.checked;
      if (node.children) {
        assignAllNodes(Status.checked, node.children);
      }
    } else if (node.children) {
      checkSelectionsForEveryNode(node.children, selections);

      if (isEveryChildChecked(node.children)) {
        node.checked = checkStatus.checked;
      } else if (isSomeChildCheckedOrIndeterminate(node.children)) {

        node.checked = checkStatus.indeterminate;
      } else {
        node.checked = checkStatus.unchecked;
      }
    }
  }
  return nodes;
}

export function isNodeSelected(selections: Selections, node: BaseNode) {
  let nodeIsSelected;

  if (selections.has(node.value)) {
    nodeIsSelected = true;
  } else {
    const selectionsList = [...selections];
    nodeIsSelected = selectionsList.some(selection => node.value.startsWith(selection + '|'));
  }
  return nodeIsSelected;
}

export function checkSelectionsForNode(root: NodeSet, selections: Selections, keys: string[], parentNode?: BaseNode) {
  const matchNode = root?.find((item: BaseNode) => item.value === keys[0]);    
  if (!matchNode) {
    throw new Error("Node not found!");
  }
  
  if (keys.length > 1) {
    keys[1] = `${keys[0]}|${keys[1]}`;
    const subKeys = keys.slice(1, keys.length);
    if (matchNode.children) {
      checkSelectionsForNode(matchNode.children, selections, subKeys, matchNode);
    }
    if (parentNode) {
      checkNodeAbove(parentNode);
    }
    
  } else {
    const isSelected = isNodeSelected(selections, matchNode);
    matchNode.checked = isSelected ? checkStatus.checked : checkStatus.unchecked;      
    if (parentNode) {
      checkNodeAbove(parentNode);
    }
    if (matchNode.children) {
      if (isSelected) {
        assignAllNodes(Status.checked, matchNode.children);
      } else {
        checkSelectionsForEveryNode(matchNode.children, selections);
        checkNodeAbove(matchNode);
        if (parentNode) {
          checkNodeAbove(parentNode);
        }
      }
    }
  }
  return root;
}

export function findNode(root: NodeSet, node: BaseNode): BaseNode {
  return getNode(root, node);
}

export function getNode(root: NodeSet, node: BaseNode): BaseNode {
  const pathKeys = node.value.split('|');
  return pathKeys.reduce((data: any, _currentValue, currentIndex, keys) => {
    const nodes = Array.isArray(data) ? data : data.children;
    const value = keys.slice(0, (currentIndex + 1)).join('|');
    return nodes?.find((item: any) => item.value === value);
  }, root);
}

export function setNodeChildren(root: NodeSet, selections: Selections, node: BaseNode, children: NodeSet) {
  const keys = node.value.split('|');
  const matchNode = getNode(root, node);
  if (matchNode) {
    matchNode.children = children;
    checkSelectionsForNode(root, selections, keys);
  }
  return root;
}

export function updateSelections(selections: Selections, nodesToBeAdded: string[], nodesToBeDeleted: string[]) {
  for(const nodeValue of nodesToBeAdded) {
    selections.add(nodeValue);
  }
  for(const nodeValue of nodesToBeDeleted) {
    selections.delete(nodeValue);
  }
  return selections;
}

export function assignAllNodes(status: Status, nodes: NodeSet) {
  const check = checkStatus[status];
  return nodes?.map(node => {
    node.checked = check;
    if (node.children) {
      node.children = assignAllNodes(status, node.children);
    }
    return node;
  });
}

export function checkAll(root: NodeSet) {
  return assignAllNodes(Status.checked, root);
}

export function uncheckAll(root: NodeSet) {
  return assignAllNodes(Status.unchecked, root);
}

export function isNodeUnchecked(node: BaseNode) {
  return node.checked && !node.checked[0] && !node.checked[1];
}

export function isNodeChecked(node: BaseNode) {
  return node.checked && node.checked[0] && node.checked[1];
}

export function isNodeIndeterminate(node: BaseNode) {
  return node.checked && node.checked[0] && !node.checked[1];
}

export function isEveryChildChecked(nodes: NodeSet) {
  return nodes && nodes.length && !nodes.some((child: BaseNode) => !isNodeChecked(child));
}

export function isSomeChildCheckedOrIndeterminate(nodes: NodeSet) {
  return nodes.some((child: BaseNode) => isNodeChecked(child) || isNodeIndeterminate(child));
}

export function checkNodeAbove(parentNode: BaseNode) {
  if (parentNode && parentNode.children && parentNode.children.length) {
    if (isEveryChildChecked(parentNode.children)) {
      parentNode.checked = checkStatus.checked;
    } else if (isSomeChildCheckedOrIndeterminate(parentNode.children)) {
      parentNode.checked = checkStatus.indeterminate;
    } else {
      parentNode.checked = checkStatus.unchecked;
    }
  }
}

export function checkNodesBelow(nodes: NodeSet, selectionsSet: Set<string>, check: boolean) {
  if (nodes && nodes.length) {
    for(const node of nodes) {
      selectionsSet.delete(node.value);
      node.checked = check ? checkStatus.checked : checkStatus.unchecked;

      if (node.children && node.children.length) {
        checkNodesBelow(node.children, selectionsSet, check);
      }
    }
  }
}

export function checkChange(root: NodeSet, selections: Selections, node: BaseNode, checked: boolean): [NodeSet, Selections] {
  const keys = node.value.split('|');
  const [ selectedNodes, deselectedNodes ] = checkPath(root, selections, keys, checked);
  const newSelections = updateSelections(selections, selectedNodes, deselectedNodes);
  return [root, newSelections];
}

function deselectAnyNodeBelow(selections: Selections, parentNode: BaseNode) {
  const selectionsList = Array.from(selections);
  for(const item of selectionsList) {
    if (item.startsWith(parentNode.value + '|')) {
      selections.delete(item);
    }
  }
  return selections;
}

export function checkPath(root: NodeSet, selections: Selections, keys: string[], checked: boolean, parentNode?: BaseNode): [string[], string[]] {
  const matchNode = root?.find((item: BaseNode) => item.value === keys[0]);    
  if (!matchNode) {
    throw new Error("Node not found!");
  }

  if (keys.length > 1) {
    keys[1] = `${keys[0]}|${keys[1]}`;
    const subKeys = keys.slice(1, keys.length);

    const nodeWasChecked = isNodeChecked(matchNode);
    const [selectedNodeChildren, deselectedNodeChildren] = matchNode.children ? checkPath(matchNode.children, selections, subKeys, checked, matchNode) : [[],[]];
    if (parentNode) {
      checkNodeAbove(parentNode);
    }

    if (checked) {
      if (isNodeChecked(matchNode)) {
        const selectedNodes = [matchNode.value];
        let deselectedNodes = matchNode.children ? matchNode.children.filter((node: BaseNode) => node.value !== selectedNodeChildren[0]).map((node: BaseNode) => node.value) : [];
        deselectedNodes = [...deselectedNodeChildren, ...deselectedNodes];
        return [selectedNodes, deselectedNodes];
      } else {
        return [selectedNodeChildren, deselectedNodeChildren]
      }
    } else {
      if (nodeWasChecked) {
        const deselectedNodes = [matchNode.value];
        let selectedNodes = matchNode.children ? matchNode.children.filter((node: BaseNode) => node.value !== deselectedNodeChildren[0]).map((node: BaseNode) => node.value) : [];
        selectedNodes = [...selectedNodeChildren, ...selectedNodes];
        return [selectedNodes, deselectedNodes];
      } else {
        return [selectedNodeChildren, deselectedNodeChildren];
      }
    }
  } else {
    matchNode.checked = checked ? checkStatus.checked : checkStatus.unchecked;      
    if (parentNode) {
      checkNodeAbove(parentNode);
    }
    if (matchNode.children) {
      checkNodesBelow(matchNode.children, selections, checked);
    }
    deselectAnyNodeBelow(selections, matchNode);
    
    return checked ? [[matchNode.value],[]] : [[],[matchNode.value]];
  }
}

export function invertNodeChildren(root: NodeSet, selections: Selections, node: BaseNode): [NodeSet, Selections]  {
  const selectedNode = findNode(root, node);
  let newRoot: NodeSet = root;
  let newSelections: Selections = selections;
  
  if (selectedNode.children) {
    for (const child of selectedNode.children) {
      if (isNodeChecked(child)) {
        [newRoot, newSelections] = checkChange(newRoot, newSelections, child, false);
      } else {
        [newRoot, newSelections] = checkChange(newRoot, newSelections, child, true);
      }
    }
  } else {
    if (isNodeChecked(node)) {
      [newRoot, newSelections] = checkChange(newRoot, newSelections, node, false);
    } else {
      [newRoot, newSelections] = checkChange(newRoot, newSelections, node, true);
    }
  }
  return [newRoot, newSelections];
}

export function checkNodeChildren(root: NodeSet, selections: Selections, node: BaseNode): [NodeSet, Selections]  {
  let newRoot: NodeSet = root;
  let newSelections: Selections = selections;
  let status = true;
  
  if (isNodeChecked(node)) {
    status = false;
  }
  
  if (node.children) {
    for (const child of node.children) {
      [newRoot, newSelections] = checkChange(newRoot, newSelections, child, status);
    }
  } else {
    [newRoot, newSelections] = checkChange(newRoot, newSelections, node, status);
  }
  return [newRoot, newSelections];
}